import React, { useEffect } from 'react';
import { PageHero } from '../components/page/PageHero';
import { IncludedList } from '../components/page/IncludedList';
import { WhyLawyerLed } from '../components/page/WhyLawyerLed';
import { FeeTransparency } from '../components/page/FeeTransparency';
import { MiniFAQ } from '../components/page/MiniFAQ';
import { FinalCTA } from '../components/home/FinalCTA';
import { Button } from '../components/ui/Button';
import { useDrawers } from '../contexts/DrawerContext';
import { FileText, Clock, ShieldCheck } from 'lucide-react';
const INCLUDED = [{
  title: 'Special conditions explained',
  body: 'Finance, building and pest, subject-to-sale and other conditions reviewed so you know what you are agreeing to.'
}, {
  title: 'Disclosure documents explained',
  body: 'The relevant disclosure material is read alongside the contract so gaps and concerns are identified.'
}, {
  title: 'Title and property risks',
  body: 'Easements, covenants, owners corporation and similar matters considered as part of the review.'
}, {
  title: 'Plain-English summary',
  body: 'You receive a clear explanation of the key risks and anything that needs attention before you sign.'
}];
const FAQS = [{
  q: 'How quickly can a contract be reviewed?',
  a: 'Timing depends on the document, transaction and current workload. Include your signing or auction deadline when submitting the contract so the team can assess urgency.'
}, {
  q: 'Can you review before auction?',
  a: 'Yes. The contract should ideally be reviewed before auction day because auction contracts are generally unconditional once the property is purchased. Send the contract and auction date with your enquiry.'
}, {
  q: 'Does a review create a solicitor-client relationship?',
  a: 'Submitting information does not by itself create a solicitor-client relationship. Engagement is confirmed separately, with the scope and fee set out before any work begins.'
}];
const ASSURANCES = [{
  icon: FileText,
  label: 'PDF, DOC and DOCX accepted'
}, {
  icon: Clock,
  label: 'Tell us your deadline and we assess urgency'
}, {
  icon: ShieldCheck,
  label: 'Your documents handled confidentially'
}];
export function ContractReview() {
  const {
    openUpload
  } = useDrawers();
  useEffect(() => {
    document.title = 'Contract Review and Disclosure Advice | L.A Property Law';
    window.scrollTo(0, 0);
  }, []);
  return <main data-id="element-626" data-mp="2pt59sqzndt51">
      <PageHero eyebrow="contract review" title="Understand the contract before you sign." intro="A lawyer-led review of your contract of sale and relevant disclosure documents, with the special conditions, disclosures and key risks explained in plain English." data-id="element-627" data-mp="2ptf2imc2ueef" />

      <section className="px-5 md:px-6 pb-4" data-id="element-628" data-mp="3dig1zk2xmf7k">
        <div className="max-w-7xl mx-auto" data-id="element-629" data-mp="3djhn6hdnwi30">
          <div className="rounded-2xl bg-surface border border-border px-5 py-5 grid sm:grid-cols-3 gap-4" data-id="element-630" data-mp="38rzqi5io93nu">
            {ASSURANCES.map(a => {
            const Icon = a.icon;
            return <div key={a.label} className="flex items-center gap-3" data-id="element-631" data-mp="3l1z2j487yr3w">
                  <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full bg-accent-soft text-accent" data-id="element-632" data-mp="3l4bkvihw33hh">
                    <Icon className="w-5 h-5" data-id="element-633" data-mp="3v0y3s1qnnkgv" />
                  </span>
                  <span className="text-sm text-ink font-medium leading-snug" data-id="element-634" data-mp="3uy26mfmmszth">
                    {a.label}
                  </span>
                </div>;
          })}
          </div>
        </div>
      </section>

      <IncludedList eyebrow="what you get" heading="A review that tells you what actually matters." intro="Not a checkbox exercise. A clear read of the documents and the risks that could affect your decision." items={INCLUDED} data-id="element-635" data-mp="clxap1xeyidr" />

      <WhyLawyerLed data-id="element-636" data-mp="mis7nge1gapx" />

      <FeeTransparency variant="cream" data-id="element-637" data-mp="rjg4iioy7ls2" />

      <section className="px-5 md:px-6 py-10 md:py-14 bg-background" data-id="element-638" data-mp="rk7rj54hcb6q">
        <div className="max-w-3xl mx-auto text-center" data-id="element-639" data-mp="1oabawgobtvxx">
          <p className="eyebrow text-brand-blue mb-1" data-id="element-640" data-mp="1o8qf6nqa06yh">
            ready when you are
          </p>
          <h2 className="font-serif text-3xl md:text-[2.6rem] text-ink leading-tight" data-id="element-641" data-mp="1ja5p3h4qn3nr">
            Send the contract and we will take it from there.
          </h2>
          <p className="mt-4 text-lg text-muted-ink leading-relaxed" data-id="element-642" data-mp="1t34daxwbos6u">
            Upload the contract and any disclosure documents, add any auction or
            signing deadline, and the team will confirm the scope and fee before
            any work begins.
          </p>
          <div className="mt-8 flex justify-center" data-id="element-643" data-mp="1ed5j167w20wn">
            <Button onClick={openUpload} variant="accent" size="lg" data-id="element-644" data-mp="1easuubbjeub5">
              <FileText className="w-5 h-5" data-id="element-645" data-mp="1aaijej4z7bow" />
              Upload a contract
            </Button>
          </div>
        </div>
      </section>

      <MiniFAQ items={FAQS} data-id="element-646" data-mp="2lqh2lftvbe3u" />
      <FinalCTA data-id="element-647" data-mp="2lr8mhte3rnea" />
    </main>;
}